import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const changeOrderDashboardUrl = '/crm/dashboard/change_orders';

export const fetchChangeOrderSummary = createAsyncThunk(
  'dashboardChangeOrder/fetchSummary',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${changeOrderDashboardUrl}/get_summary_data`, {
        params
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch summary data')
      );
    }
  }
);

export const fetchCSEStatistics = createAsyncThunk(
  'dashboardChangeOrder/fetchCSEStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/get_cse_incharge_data`,
        { params }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch CSE statistics')
      );
    }
  }
);

export const fetchStatusDistribution = createAsyncThunk(
  'dashboardChangeOrder/fetchStatusDistribution',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/get_status_data`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch status distribution')
      );
    }
  }
);

export const fetchShopDrawingAnalysis = createAsyncThunk(
  'dashboardChangeOrder/fetchShopDrawingAnalysis',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/get_shop_drawing_data`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch shop drawing analysis')
      );
    }
  }
);

export const fetchFilterOptions = createAsyncThunk(
  'dashboardChangeOrder/fetchFilterOptions',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/filter_options`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch filter options')
      );
    }
  }
);

export const fetchDetailedChangeOrders = createAsyncThunk(
  'dashboardChangeOrder/fetchDetailedChangeOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/get_detailed_data`,
        { params }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch detailed change orders')
      );
    }
  }
);

export const fetchSalesUnitStatistics = createAsyncThunk(
  'dashboardChangeOrder/fetchSalesUnitStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/get_sales_unit_data`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch sales unit statistics')
      );
    }
  }
);

export const exportDetailedChangeOrders = createAsyncThunk(
  'dashboardChangeOrder/exportDetailedChangeOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/export_detailed_data`,
        {
          params,
          responseType: 'blob'
        }
      );

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `change-orders-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to export change orders')
      );
    }
  }
);

// Objective KPIs (Schedule / Order Entry / JAF vs EST) — moved here from the
// Project Schedule (OE) report. One request feeds the scorecard and all three
// tabs; they count JOBS (P/J number), not change-order rows.
export const fetchObjectives = createAsyncThunk(
  'dashboardChangeOrder/fetchObjectives',
  // `signal` lets the dashboard abort a superseded roll-up instead of only
  // ignoring its answer — the server-side job grouping is the expensive part.
  async (params, { rejectWithValue, signal }) => {
    try {
      const response = await apiAxios.get(`${changeOrderDashboardUrl}/get_objectives`, {
        params,
        signal,
        timeout: 120000,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to fetch objective KPIs'));
    }
  }
);

export const exportObjectives = createAsyncThunk(
  'dashboardChangeOrder/exportObjectives',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${changeOrderDashboardUrl}/export_objectives`, {
        params,
        responseType: 'blob',
        timeout: 180000,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `change-order-objectives-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to export objective KPIs'));
    }
  }
);

export const fetchDashboardOverview = createAsyncThunk(
  'dashboardChangeOrder/fetchOverview',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${changeOrderDashboardUrl}/index`,
        { params }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        handleAsyncThunkError(error, 'Failed to fetch dashboard overview')
      );
    }
  }
);

const initialState = {
  // Summary data
  summary: {
    data: null,
    loading: false,
    error: null
  },

  // CSE Statistics
  cseStatistics: {
    data: [],
    pagination: {
      currentPage: 1,
      perPage: 50,
      totalRecords: 0,
      totalPages: 0
    },
    loading: false,
    error: null
  },

  // Status Distribution
  statusDistribution: {
    data: [],
    loading: false,
    error: null
  },

  // Shop Drawing Analysis
  shopDrawingAnalysis: {
    data: [],
    loading: false,
    error: null
  },

  // Filter Options
  filterOptions: {
    data: null,
    loading: false,
    error: null
  },

  // Sales Unit Statistics
  salesUnitStatistics: {
    data: [],
    loading: false,
    error: null
  },

  // Objective KPIs — `requestId` drops a stale response when filters change
  // faster than the (heavy) job roll-up answers.
  objectives: {
    data: null,
    loading: false,
    error: null,
    requestId: null
  },

  // Own flag so the objectives export never shows the detailed-list export
  // as busy (or leaks its error into it), and vice versa.
  objectivesExport: {
    loading: false,
    error: null
  },

  // Detailed Change Orders
  detailedChangeOrders: {
    data: [],
    pagination: {
      current_page: 1,
      per_page: 50,
      total_records: 0,
      total_pages: 0
    },
    loading: false,
    error: null
  },

  // Export
  export: {
    loading: false,
    error: null
  },

  // Overall dashboard state
  loading: false,
  error: null,
  lastUpdated: null
};

const dashboardChangeOrderSlice = createSlice({
  name: 'dashboardChangeOrder',
  initialState,
  reducers: {
    resetSummary: (state) => {
      state.summary = initialState.summary;
    },
    resetCSEStatistics: (state) => {
      state.cseStatistics = initialState.cseStatistics;
    },
    resetStatusDistribution: (state) => {
      state.statusDistribution = initialState.statusDistribution;
    },
    resetDetailedChangeOrders: (state) => {
      state.detailedChangeOrders = initialState.detailedChangeOrders;
    },
    resetError: (state) => {
      state.error = null;
      state.summary.error = null;
      state.cseStatistics.error = null;
      state.statusDistribution.error = null;
      state.shopDrawingAnalysis.error = null;
      state.filterOptions.error = null;
      state.detailedChangeOrders.error = null;
    }
  },
  extraReducers: (builder) => {
    // fetchChangeOrderSummary
    builder
      .addCase(fetchChangeOrderSummary.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(fetchChangeOrderSummary.fulfilled, (state, action) => {
        state.summary.data = action.payload;
        state.summary.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchChangeOrderSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload;
      });

    // fetchCSEStatistics
    builder
      .addCase(fetchCSEStatistics.pending, (state) => {
        state.cseStatistics.loading = true;
        state.cseStatistics.error = null;
      })
      .addCase(fetchCSEStatistics.fulfilled, (state, action) => {
        state.cseStatistics.data = action.payload.data || [];
        state.cseStatistics.pagination = action.payload.pagination || initialState.cseStatistics.pagination;
        state.cseStatistics.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchCSEStatistics.rejected, (state, action) => {
        state.cseStatistics.loading = false;
        state.cseStatistics.error = action.payload;
      });

    // fetchStatusDistribution
    builder
      .addCase(fetchStatusDistribution.pending, (state) => {
        state.statusDistribution.loading = true;
        state.statusDistribution.error = null;
      })
      .addCase(fetchStatusDistribution.fulfilled, (state, action) => {
        state.statusDistribution.data = action.payload;
        state.statusDistribution.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchStatusDistribution.rejected, (state, action) => {
        state.statusDistribution.loading = false;
        state.statusDistribution.error = action.payload;
      });

    // fetchShopDrawingAnalysis
    builder
      .addCase(fetchShopDrawingAnalysis.pending, (state) => {
        state.shopDrawingAnalysis.loading = true;
        state.shopDrawingAnalysis.error = null;
      })
      .addCase(fetchShopDrawingAnalysis.fulfilled, (state, action) => {
        state.shopDrawingAnalysis.data = action.payload;
        state.shopDrawingAnalysis.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchShopDrawingAnalysis.rejected, (state, action) => {
        state.shopDrawingAnalysis.loading = false;
        state.shopDrawingAnalysis.error = action.payload;
      });

    // fetchFilterOptions
    builder
      .addCase(fetchFilterOptions.pending, (state) => {
        state.filterOptions.loading = true;
        state.filterOptions.error = null;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions.data = action.payload;
        state.filterOptions.loading = false;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        state.filterOptions.loading = false;
        state.filterOptions.error = action.payload;
      });

    // fetchSalesUnitStatistics
    builder
      .addCase(fetchSalesUnitStatistics.pending, (state) => {
        state.salesUnitStatistics.loading = true;
        state.salesUnitStatistics.error = null;
      })
      .addCase(fetchSalesUnitStatistics.fulfilled, (state, action) => {
        state.salesUnitStatistics.data = action.payload || [];
        state.salesUnitStatistics.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSalesUnitStatistics.rejected, (state, action) => {
        state.salesUnitStatistics.loading = false;
        state.salesUnitStatistics.error = action.payload;
      });

    // fetchDetailedChangeOrders
    builder
      .addCase(fetchDetailedChangeOrders.pending, (state) => {
        state.detailedChangeOrders.loading = true;
        state.detailedChangeOrders.error = null;
      })
      .addCase(fetchDetailedChangeOrders.fulfilled, (state, action) => {
        state.detailedChangeOrders.data = action.payload.data;
        state.detailedChangeOrders.pagination = action.payload.pagination;
        state.detailedChangeOrders.loading = false;
      })
      .addCase(fetchDetailedChangeOrders.rejected, (state, action) => {
        state.detailedChangeOrders.loading = false;
        state.detailedChangeOrders.error = action.payload;
      });

    // fetchObjectives
    builder
      .addCase(fetchObjectives.pending, (state, action) => {
        state.objectives.loading = true;
        state.objectives.error = null;
        state.objectives.requestId = action.meta.requestId;
      })
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        if (state.objectives.requestId !== action.meta.requestId) return;
        state.objectives.data = action.payload || null;
        state.objectives.loading = false;
      })
      .addCase(fetchObjectives.rejected, (state, action) => {
        if (state.objectives.requestId !== action.meta.requestId) return;
        state.objectives.loading = false;
        if (action.payload?.cancelled) return;
        state.objectives.error = action.payload?.error || 'Unable to load objective KPIs.';
      });

    // exportObjectives
    builder
      .addCase(exportObjectives.pending, (state) => {
        state.objectivesExport.loading = true;
        state.objectivesExport.error = null;
      })
      .addCase(exportObjectives.fulfilled, (state) => {
        state.objectivesExport.loading = false;
      })
      .addCase(exportObjectives.rejected, (state, action) => {
        state.objectivesExport.loading = false;
        state.objectivesExport.error = action.payload;
      });

    // exportDetailedChangeOrders
    builder
      .addCase(exportDetailedChangeOrders.pending, (state) => {
        state.export.loading = true;
        state.export.error = null;
      })
      .addCase(exportDetailedChangeOrders.fulfilled, (state) => {
        state.export.loading = false;
      })
      .addCase(exportDetailedChangeOrders.rejected, (state, action) => {
        state.export.loading = false;
        state.export.error = action.payload;
      });

    // fetchDashboardOverview
    builder
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        const { summary, status_distribution, cse_statistics, shop_drawing_analysis } = action.payload;
        state.summary.data = summary;
        state.statusDistribution.data = status_distribution;
        state.cseStatistics.data = cse_statistics;
        state.shopDrawingAnalysis.data = shop_drawing_analysis;
        state.loading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetSummary, resetCSEStatistics, resetStatusDistribution, resetDetailedChangeOrders, resetError } = dashboardChangeOrderSlice.actions;

export default dashboardChangeOrderSlice.reducer;
